import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useNotify } from '@/hooks/useNotify';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

export type ProjectStatus = 
  | 'planning' 
  | 'pending_start' 
  | 'in_progress' 
  | 'on_hold' 
  | 'delayed' 
  | 'completed' 
  | 'cancelled';

interface ProjectStatusDropdownProps {
  projectId: string;
  currentStatus: string | null;
  onStatusChange?: (newStatus: ProjectStatus) => void;
}

export const STATUS_CONFIG: Record<ProjectStatus, { label: string; color: string; bgClass: string }> = {
  planning: { 
    label: 'Planning', 
    color: 'text-gray-700', 
    bgClass: 'bg-gray-100 border-gray-300 hover:bg-gray-200' 
  },
  pending_start: { 
    label: 'Pending Start', 
    color: 'text-yellow-700', 
    bgClass: 'bg-yellow-100 border-yellow-300 hover:bg-yellow-200' 
  },
  in_progress: { 
    label: 'In Progress', 
    color: 'text-green-700', 
    bgClass: 'bg-green-100 border-green-300 hover:bg-green-200' 
  },
  on_hold: { 
    label: 'On Hold', 
    color: 'text-orange-700', 
    bgClass: 'bg-orange-100 border-orange-300 hover:bg-orange-200' 
  },
  delayed: { 
    label: 'Delayed', 
    color: 'text-red-600', 
    bgClass: 'bg-red-100 border-red-300 hover:bg-red-200' 
  },
  completed: { 
    label: 'Completed', 
    color: 'text-blue-700', 
    bgClass: 'bg-blue-100 border-blue-300 hover:bg-blue-200' 
  },
  cancelled: { 
    label: 'Cancelled', 
    color: 'text-red-700', 
    bgClass: 'bg-red-100 border-red-300 hover:bg-red-200' 
  },
};

export const getStatusBadge = (status: string | null) => {
  const normalizedStatus = (status?.toLowerCase() || 'planning') as ProjectStatus;
  const config = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.planning;
  
  return (
    <Badge variant="outline" className={`${config.bgClass} ${config.color} border`}>
      {config.label}
    </Badge>
  );
};

const ProjectStatusDropdown: React.FC<ProjectStatusDropdownProps> = ({
  projectId,
  currentStatus,
  onStatusChange,
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<ProjectStatus | null>(null);
  const [statusNotes, setStatusNotes] = useState('');

  const normalizedStatus = (currentStatus?.toLowerCase() || 'planning') as ProjectStatus;

  const updateStatusMutation = useMutation({
    mutationFn: async ({ newStatus, notes }: { newStatus: ProjectStatus; notes?: string }) => {
      const { error } = await supabase
        .from('projects')
        .update({ status: newStatus })
        .eq('id', projectId);

      if (error) throw error;

      // Manually insert notes into history if provided
      if (notes) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('project_status_history')
            .update({ notes })
            .eq('project_id', projectId)
            .eq('new_status', newStatus)
            .order('changed_at', { ascending: false })
            .limit(1);
        }
      }

      return newStatus;
    },
    onSuccess: (newStatus) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projectStatusHistory', projectId] });
      notify.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
      onStatusChange?.(newStatus);
    },
    onError: (error) => {
      console.error('Error updating project status:', error);
      notify.error(error);
    },
  });

  const handleStatusChange = (value: string) => {
    const newStatus = value as ProjectStatus;
    
    // For certain status changes, prompt for notes
    if (newStatus === 'on_hold' || newStatus === 'cancelled' || newStatus === 'delayed') {
      setPendingStatus(newStatus);
      setShowNotesDialog(true);
    } else {
      updateStatusMutation.mutate({ newStatus });
    }
  };

  const handleConfirmStatusChange = () => {
    if (pendingStatus) {
      updateStatusMutation.mutate({ newStatus: pendingStatus, notes: statusNotes });
      setShowNotesDialog(false);
      setPendingStatus(null);
      setStatusNotes('');
    }
  };

  const handleCancelStatusChange = () => {
    setShowNotesDialog(false);
    setPendingStatus(null);
    setStatusNotes('');
  };

  return (
    <>
      <Select
        value={normalizedStatus}
        onValueChange={handleStatusChange}
        disabled={updateStatusMutation.isPending}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue>
            {getStatusBadge(normalizedStatus)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(STATUS_CONFIG) as ProjectStatus[]).map((status) => (
            <SelectItem key={status} value={status}>
              <div className="flex items-center gap-2">
                {getStatusBadge(status)}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Change Status to {pendingStatus ? STATUS_CONFIG[pendingStatus].label : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="status-notes">Reason for status change (optional)</Label>
              <Textarea
                id="status-notes"
                placeholder="Enter reason for this status change..."
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelStatusChange}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmStatusChange}
              disabled={updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectStatusDropdown;
